'use client'
import React from 'react';
import { Container, Heading, Text, Box, Stack } from '@chakra-ui/react';

const TermsPrivacyRefund = () => {
	return (
		<Container maxW="7xl" py={10}>
			<Stack spacing={8}>
				<Heading as="h1" size="xl" textAlign="center" bgGradient='linear(to-b, #FF0080, #7928CA)' bgClip='text' _hover={{bgGradient: 'linear(to-r, red.500, purple.500)'}} >
					Terms & Conditions, Privacy Policy, and Refund Policy
				</Heading>

				<Text fontSize="sm" color="gray.500" textAlign="center">
					Last Updated: December 25th, 2024
				</Text>

				<Box textAlign="center" >
					<Heading as="h2" size="lg" py={4} >
						1. Terms & Conditions
					</Heading>
					<Heading as="h3" size="md" py={2} >
						1.1 Definitions
					</Heading>
					<Text >
						 <strong>Client:</strong> A person or entity booking photography services via the Platform.<br />
						 <strong>Photographer:</strong> A professional or freelance photographer offering services via the Platform.<br />
						 <strong>Platform:</strong> The PapiRazzi web application and any associated services.
					</Text>

					<Heading as="h3" size="md" py={2} >
						1.2 Scope of Services
					</Heading>
					<Text >
						PapiRazzi connects Clients with Photographers for photography services. We act as an intermediary and are not responsible for the quality, completion, or results of any photography services provided by Photographers.
					</Text>

					<Heading as="h3" size="md" py={2} >
						1.3 User Obligations
					</Heading>
					<Text >
						By using the Platform, you agree to:
						<br />
						1. Provide accurate information during registration and booking.<br />
						2. Refrain from using the Platform for any illegal or unauthorized purposes.<br />
						3. Respect the intellectual property rights of the Platform and its users.<br />
						4. Adhere to all applicable laws, rules, and regulations.
					</Text>
					<br />

					<Heading as="h3" size="md" py={2} >
						1.4 Booking and Payments
					</Heading>
					<Text >
						1. Stripe System: Payments are held in Stripe until the service is completed and the Client approves the final deliverables. <br />
						2. Photographer Obligations: Photographers must deliver high-quality images as agreed upon during the booking process. <br />
						3. Client Approval: Clients must review and approve the deliverables within 7 days, or funds will be released automatically to the Photographer.
					</Text>
					<br />

					<Heading as="h3" size="md" py={2} >
						1.5 Cancellation Policy
					</Heading>
					<Text >
						1. Clients: Cancellations made within 24 hours of the booking time will incur a cancellation fee of [Insert % or Flat Fee]. <br />
						2. Photographers: Repeated cancellations or no-shows may result in account suspension or termination. <br />
						3. Client Approval: Clients must review and approve the deliverables within 7 days, or funds will be released automatically to the Photographer.
					</Text>
					<br />

					<Heading as="h3" size="md" py={2} >
						1.6 Intellectual Property
					</Heading>
					<Text >
						All content provided by Photographers remains their intellectual property unless explicitly agreed otherwise. The Platform may use anonymized images for promotional purposes with user consent.
					</Text>
					<br />

					<Heading as="h3" size="md" py={2} >
						1.7 Limitation of Liability
					</Heading>
					<Text >
						The Platform is not liable for any disputes, damages, or losses arising from agreements between Clients and Photographers.
					</Text>
					{/* Add other subsections of Terms & Conditions here */}
				</Box>

				<Box textAlign="center">
					<Heading as="h2" size="lg" py={4}>
						2. Privacy Policy
					</Heading>
					<Heading as="h3" size="md" py={2}>
						2.1 Data Collection
					</Heading>
					<Text>
						We collect the following types of data:
						<br />
						 <strong>Personal Information:</strong> Name, email address, phone number, payment details, etc.
						<br />
						 <strong>Usage Data:</strong> Browser type, device information, IP address, etc.
						<br />
						 <strong>Booking Data:</strong> Information about services booked, locations, and payment history.
					</Text>

					<Heading as="h3" size="md" py={2}>
						2.2 Use of Data
					</Heading>
					<Text>
						We use the collected data to:
						<br />
						1. Facilitate bookings and payments.<br />
						2. Improve user experience on the Platform.<br />
						3. Send notifications and updates regarding services.<br />
						4. Comply with legal obligations.
					</Text>
					<br />

					<Heading as="h3" size="md" py={2}>
            2.3 Data Sharing
          </Heading>
          <Text>
					We do not sell your personal information. However, we may share your data with:
            <br />
            1. Photographers: For fulfilling bookings.<br />
            2. Payment Processors: To facilitate secure payments.<br />
            3. Law Enforcement: If required by law.
          </Text>
					<br/>

					<Heading as="h3" size="md" py={2} >
					2.4 Data Security
					</Heading>
					<Text >
					We use industry-standard encryption and security protocols to protect your data. However, no system is completely secure, and we cannot guarantee absolute data security.
					</Text>
					<br/>

					<Heading as="h3" size="md" py={2} >
					2.5 User Rights
					</Heading>
					<Text >
					1. Access, update, or delete your personal information through your account.<br/>
					2. Opt-out of marketing communications at any time.<br/>
					3. Submit data-related concerns to our support team at <a
							href={`mailto:papi@papirazzi.app?subject=${encodeURIComponent(
								'User Rights Data Concerns'
							)}&body=${encodeURIComponent(
								'Hello PapiRazzi Support Team,\n\nI have a question regarding your user data. Could you please assist me with the following:\n\n[Your Question Here]\n\nThank you,\n[Your Name]'
							)}`}
							style={{ color: '#FF0080', textDecoration: 'underline' }}
						>
							papi@papirazzi.app
						</a>.
					</Text>
					{/* Add other subsections of Privacy Policy here */}
				</Box>

				<Box textAlign="center">
					<Heading as="h2" size="lg" py={4}>
						3. Refund Policy
					</Heading>
					<Heading as="h3" size="md" py={2}>
						3.1 Eligibility for Refunds
					</Heading>
					<Text>
						Refunds may be issued in the following cases:
						<br />
						1. The Photographer fails to deliver agreed-upon deliverables within the specified timeline.<br />
						2. The deliverables are significantly below the agreed-upon quality standards.<br />
						3. The booking is canceled by the Client or Photographer under specific conditions outlined in our cancellation policy.
					</Text>
					<br />
					<Heading as="h3" size="md" py={2}>
						3.2 Refund Process
					</Heading>
					<Text>
						1. Clients must submit a refund request within 14 days of the service date by contacting <a
							href={`mailto:papi@papirazzi.app?subject=${encodeURIComponent(
								'User Rights Data Concerns'
							)}&body=${encodeURIComponent(
								'Hello PapiRazzi Support Team,\n\nI have a question regarding your user data. Could you please assist me with the following:\n\n[Your Question Here]\n\nThank you,\n[Your Name]'
							)}`}
							style={{ color: '#FF0080', textDecoration: 'underline' }}
						>
							papi@papirazzi.app
						</a>.<br />
						2. Refund requests must include booking details and supporting evidence (e.g., screenshots, descriptions).<br />
						3. Refunds are subject to investigation and approval by our team.
					</Text>
					
					<br />
					<Heading as="h3" size="md" py={2}>
					3.3 Non-Refundable Situations
					</Heading>
					<Text>
						1. Services completed and approved by the Client.<br/>
						2. Issues unrelated to the Photographer’s performance (e.g., weather, venue problems).<br/>
						3. Cancellation fees for last-minute Client cancellations. <br/>
					</Text>
					{/* Add other subsections of Refund Policy here */}
				</Box>

				<Box textAlign="center">
					<Heading as="h2" size="lg" py={4}>
						4. Contact Information
					</Heading>
					<Text>
						For questions or concerns regarding this Agreement, please contact us at:
						<br />
						 <strong>Email:</strong>{' '}
						<a
							href={`mailto:papi@papirazzi.app?subject=${encodeURIComponent(
								'Inquiry Regarding Terms and Policies'
							)}&body=${encodeURIComponent(
								'Hello PapiRazzi Support Team,\n\nI have a question regarding your terms and policies. Could you please assist me with the following:\n\n[Your Question Here]\n\nThank you,\n[Your Name]'
							)}`}
							style={{ color: '#FF0080', textDecoration: 'underline' }}
						>
							papi@papirazzi.app
						</a>
						{/*- <strong>Phone:</strong> [Insert Phone Number]<br />
            - <strong>Mail:</strong> [Insert Mailing Address]*/}
					</Text>
				</Box>

				<Text fontSize="sm" color="gray.500" textAlign="center" mt={10}>
					By using our Platform, you acknowledge that you have read, understood, and agree to these terms.
				</Text>
			</Stack>
		</Container>
	);
};

export default TermsPrivacyRefund;
